import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { ArrowRight, KeyRound, MailCheck } from "lucide-react";
import { ForgotPasswordFormData, forgotPasswordSchema } from "@/model/schemas";
import { authService } from "@/services/authService";
import { authErrorMessage } from "@/util/authError";
import { zodFormResolver } from "@/util/form";
import AuthCard from "./auth/AuthCard";
import EmailSentPanel from "./auth/EmailSentPanel";
import { ctaVariants } from "./site/CtaLink";
import { TextField as BaseTextField } from "./site/FormFields";
import TransitionLink from "./TransitionLink";

const TextField = BaseTextField<ForgotPasswordFormData>;

const backToSignIn = (
  <TransitionLink
    to="/login"
    className="mt-6 inline-block text-[15px] font-semibold text-brand-ink-strong transition-colors hover:text-navy"
  >
    Back to sign in
  </TransitionLink>
);

const ForgotPassword = () => {
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const form = useForm<ForgotPasswordFormData>({
    resolver: zodFormResolver<ForgotPasswordFormData>(forgotPasswordSchema),
    mode: "onTouched",
    defaultValues: { email: "" },
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setErrorMessage(null);
    const email = data.email.trim();
    try {
      await authService.forgotPassword(email);
      setSentTo(email);
    } catch (error) {
      setErrorMessage(authErrorMessage(error));
    }
  };

  if (sentTo) {
    return (
      <AuthCard icon={MailCheck} title="Check your email">
        <EmailSentPanel
          email={sentTo}
          message="If there's an OnHome account for this address, we've sent a link to choose a new password to"
          onResend={() => authService.forgotPassword(sentTo)}
        />
        {backToSignIn}
      </AuthCard>
    );
  }

  return (
    <AuthCard
      icon={KeyRound}
      title="Forgot your password?"
      subtitle="Enter the email you use for OnHome and we'll send you a link to choose a new one."
    >
      <FormProvider {...form}>
        <form noValidate onSubmit={form.handleSubmit(onSubmit)} className="grid gap-6">
          <TextField name="email" label="Email" type="email" autoComplete="email" placeholder="you@example.com" />
          {errorMessage && (
            <p role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
              {errorMessage}
            </p>
          )}
          <button
            type="submit"
            disabled={form.formState.isSubmitting}
            className={ctaVariants({ className: "w-full" })}
          >
            {form.formState.isSubmitting ? "Sending…" : "Send reset link"}
            <ArrowRight className="size-4" aria-hidden />
          </button>
        </form>
      </FormProvider>
      {backToSignIn}
    </AuthCard>
  );
};

export default ForgotPassword;
